import { AudioListener, PositionalAudio, Audio, type Camera, type Object3D } from 'three'
import type { Pilot } from '../physics/Pilot'

/**
 * Procedural rocket audio (DESIGN.md §7) — no assets, so nothing to load and
 * nothing to cache-bust on deploy. Per-rocket thrust loops are positional and
 * ride on the nozzle objects; wind rides on airspeed.
 */
export class ThrustAudio {
  readonly listener = new AudioListener()
  private thrust: PositionalAudio[] = []
  private wind: Audio | null = null
  private started = false

  constructor(camera: Camera) {
    camera.add(this.listener)
  }

  get running(): boolean {
    return this.started
  }

  /** Must be called from a user gesture — browsers will not start audio otherwise. */
  start(nozzles: Object3D[]): void {
    if (this.started) return
    this.started = true

    const context = this.listener.context
    void context.resume()

    const noise = makeNoiseBuffer(context, 2)

    for (const nozzle of nozzles) {
      const sound = new PositionalAudio(this.listener)
      sound.setBuffer(noise)
      sound.setLoop(true)
      sound.setRefDistance(1.2)
      sound.setVolume(0)
      // A steep low-pass gives the "roar" rather than "hiss"; the cutoff rides
      // the throttle so spooling up is audible before it is felt.
      const filter = context.createBiquadFilter()
      filter.type = 'lowpass'
      filter.frequency.value = 300
      filter.Q.value = 1.2
      sound.setFilter(filter)
      nozzle.add(sound)
      sound.play()
      this.thrust.push(sound)
    }

    const wind = new Audio(this.listener)
    wind.setBuffer(noise)
    wind.setLoop(true)
    wind.setVolume(0)
    const windFilter = context.createBiquadFilter()
    windFilter.type = 'bandpass'
    windFilter.frequency.value = 700
    windFilter.Q.value = 0.6
    wind.setFilter(windFilter)
    wind.play()
    this.wind = wind
  }

  update(pilot: Pilot): void {
    if (!this.started) return

    for (let i = 0; i < this.thrust.length; i++) {
      const level = pilot.debug.rockets[i]?.level ?? 0
      const sound = this.thrust[i]
      sound.setVolume(level * 0.55)
      const filter = sound.getFilter() as BiquadFilterNode | undefined
      if (filter) filter.frequency.value = 220 + level * 1600
      const rate = 0.75 + level * 0.5
      if (sound.playbackRate !== rate) sound.setPlaybackRate(rate)
    }

    if (this.wind) {
      const airspeed = pilot.body.velocity.length()
      this.wind.setVolume(Math.min(0.35, (airspeed / 45) ** 2 * 0.35))
      const filter = this.wind.getFilter() as BiquadFilterNode | undefined
      if (filter) filter.frequency.value = 400 + Math.min(airspeed, 60) * 26
    }
  }

  setMuted(muted: boolean): void {
    this.listener.setMasterVolume(muted ? 0 : 1)
  }
}

/** Looping white noise, smoothed slightly so it reads as rumble not static. */
function makeNoiseBuffer(context: AudioContext, seconds: number): AudioBuffer {
  const length = Math.floor(context.sampleRate * seconds)
  const buffer = context.createBuffer(1, length, context.sampleRate)
  const data = buffer.getChannelData(0)
  let previous = 0
  for (let i = 0; i < length; i++) {
    const white = Math.random() * 2 - 1
    previous = previous * 0.72 + white * 0.28
    data[i] = previous * 2.4
  }
  // Cross-fade the seam so the loop point is not an audible click.
  const fade = Math.min(2000, Math.floor(length / 8))
  for (let i = 0; i < fade; i++) {
    const t = i / fade
    data[i] = data[i] * t + data[length - fade + i] * (1 - t)
  }
  return buffer
}
